import { useState, type ReactNode } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { ProtectedRoute } from '../components/auth/index.js';
import {
  TeamAssignmentForm,
  TeamAssignmentMismatches,
  TeamAssignmentTable,
} from '../components/features/team-assignments/index.js';
import { useAuth } from '../hooks/useAuth.js';
import { useTeamAssignments } from '../hooks/useTeamAssignments.js';
import type { TeamAssignment } from '../lib/api/team-assignments.js';

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 事業×チームごとの担当（チームリーダー / DLE / DLE サブ / DLD / TechLead）を読む画面。要件定義レビューの担当とバグ票の開発ツリー子票の担当がここで決まる。参照は内部メンバー全員、設定は CTO / PMO のみ。Workspace の在籍者と突き合わせて設定漏れと対応不明の組織も出す
 * @graph-connects useTeamAssignments [calls] 担当の一覧取得
 * @graph-connects useAuth [calls] 設定権限の判定
 * @graph-connects TeamAssignmentTable [calls] 担当の一覧表
 * @graph-connects TeamAssignmentForm [calls] 担当の設定フォーム
 * @graph-connects TeamAssignmentMismatches [calls] Workspace との食い違いの表示
 */
export const Route = createFileRoute('/team-assignments')({
  component: TeamAssignmentsRoute,
});

/** @graph-connects ProtectedRoute [calls] ページアクセス可否を判定しガードする */
function TeamAssignmentsRoute(): ReactNode {
  return (
    <ProtectedRoute pageKey="teamAssignments">
      <TeamAssignmentsPage />
    </ProtectedRoute>
  );
}

/**
 * 担当の一覧と設定。
 *
 * **参照と設定で出すものを変える。** 誰が DLE かは案件を進める全員が知りたいので表は
 * 全員に見せ、編集・削除・追加のボタンだけ `MANAGE_TEAM_ASSIGNMENTS` で絞る。権限の無い人に
 * ボタンを出して 403 を返すのは、押せるのに通らないという一番分かりにくい形になる。
 * @graph-connects none
 */
function TeamAssignmentsPage(): ReactNode {
  const { t } = useTranslation();
  const auth = useAuth();
  const list = useTeamAssignments();
  const canManage = auth.can('MANAGE_TEAM_ASSIGNMENTS');

  /** 編集中の行。`null` は閉じている、`undefined` は新規。 */
  const [editing, setEditing] = useState<TeamAssignment | null | undefined>(null);
  const [presetTeam, setPresetTeam] = useState<string | undefined>(undefined);

  const close = (): void => {
    setEditing(null);
    setPresetTeam(undefined);
  };

  return (
    <div className="p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold">{t('teamAssignments.title')}</h1>
          <p className="mt-1 text-sm text-[var(--color-text-muted)]">
            {t('teamAssignments.description')}
          </p>
        </div>
        {canManage && list.data !== undefined && (
          <Button
            size="sm"
            onClick={() => {
              setEditing(undefined);
            }}
          >
            {t('teamAssignments.add')}
          </Button>
        )}
      </div>

      <div className="mt-6">
        {list.error !== null ? (
          <p className="text-sm text-[var(--color-text-secondary)]">
            {t('teamAssignments.loadFailed')}
          </p>
        ) : list.data === undefined ? (
          <p className="text-sm text-[var(--color-text-muted)]">{t('teamAssignments.loading')}</p>
        ) : (
          <>
            <TeamAssignmentTable
              assignments={list.data.assignments}
              canManage={canManage}
              onEdit={setEditing}
            />
            <TeamAssignmentMismatches
              missingTeams={list.data.missingTeams}
              unmappedOrgs={list.data.unmappedOrgs}
              devOrgs={list.data.devOrgs}
              canManage={canManage}
              onAssign={(team) => {
                setPresetTeam(team);
                setEditing(undefined);
              }}
            />
          </>
        )}
      </div>

      {list.data !== undefined && editing !== null && (
        <Modal
          open
          onClose={close}
          title={editing === undefined ? t('teamAssignments.add') : t('teamAssignments.edit')}
        >
          <TeamAssignmentForm
            options={list.data}
            {...(editing === undefined ? {} : { editing })}
            {...(presetTeam === undefined ? {} : { presetTeam })}
            onDone={close}
          />
        </Modal>
      )}
    </div>
  );
}
